import { getPageData, getPages, getLayoutData } from "../api/api"
import { useRouter } from "next/router"
import { useCallback, useMemo, useRef } from "react"
import ExtendedContent from "@/components/common/ExtendedContent"
import { duration } from "@repo/utils/gsap"
import { capitalize, getPropSafely, setCssVars } from "@repo/utils"
import { FRAGMENTS } from "@repo/ui/lib"
import { DESIGN_FRAGMENTS, DESIGN_TYPES } from "@/lib"
import dynamic from "next/dynamic"
import ProjectBanner from "@repo/ui/src/components/Banners/ProjectBanner"
import { useGlobals } from "@repo/ui/src/hooks/useGlobal"
import { useScrollContext } from "@repo/ui/src/components/Scroll"
import dynamicWithLoader from "@repo/ui/src/components/common/DynamicWithLoader"
import { useExtendedTheme } from "@repo/ui/src/components/Theme"

const StickyHeading = dynamic(() => import("@repo/ui/src/components/StickyHeading"), {
	ssr: false,
})

const NextProject = dynamicWithLoader(() => import("@repo/ui/src/components/NextProject"))

const SingleProjectElevatorNav = dynamic(() => import("@repo/ui/src/components/SingleProjectElevatorNav"), {
	ssr: false,
})

const PAGE_CONTENT_TYPE = "allDesignSingleProjects"

const pathsQuery = `
query GetSingleProject($locale: SiteLocale) {
	${PAGE_CONTENT_TYPE}(locale: $locale, first: 100) {
		name
	  	slug
	}
}
`

const propsQuery = `
	query GetProject($slug: String, $locale: SiteLocale) {
		${PAGE_CONTENT_TYPE}(locale: $locale, filter: { slug: { eq: $slug } }) {
			name
			slug
			highlight
			${FRAGMENTS.SEO_META_FRAGMENT}
			projectColor {
				hex
			}
			projectServiceRange {
				serviceName
			}
			projectCategories {
				projectCategory
				projectCategoryId
			}
			description
			bannerWhiteLogo
			bannerWhiteMenu
			year
			${FRAGMENTS.THEME_FRAGMENT}
			coverPortrait {
				${FRAGMENTS.IMAGE_FRAGMENT}
			}
			coverLandscape {
				${FRAGMENTS.IMAGE_FRAGMENT}
			}
			${DESIGN_FRAGMENTS.BREADCRUMBS_FRAGMENT}
			${DESIGN_FRAGMENTS.NAVIGATION_ELEVATOR_FRAGMENT}
	
			content {
				__typename
				${DESIGN_FRAGMENTS.TILES_MODULE_FRAGMENT}
				${DESIGN_FRAGMENTS.HEADER_AND_DESCRIPTION_FRAGMENT}
				${DESIGN_FRAGMENTS.SINGLE_ASSET_FRAGMENT}
			
				${FRAGMENTS.PADDING_MODULE_FRAGMENT}
				${FRAGMENTS.ASSETS_GALLERY_FRAGMENT}
				${FRAGMENTS.ASSET_WITH_HEADER_FRAGMENT}
				${FRAGMENTS.PROJECT_SECTION_HEADER_FRAGMENT}
				${FRAGMENTS.PROJECT_RANGE_FRAGMENT}
				${FRAGMENTS.PROJECT_SCOPE_FRAGMENT}
				${FRAGMENTS.TEXT_MODULE_FRAGMENT}
				${FRAGMENTS.PINNED_TEXT_WITH_IMAGE_FRAGMENT}
				${FRAGMENTS.PROJECT_SLIDER_FRAGMENT}
                ${FRAGMENTS.TECHNOLOGIES_TABLE_FRAGMENT}
			}
			nextProject {
				id
				tag
				projectLink {
					text
					variant
					link {
						... on ${DESIGN_TYPES.PAGES.SINGLE_PROJECT} {
							name
							slug
							description
							video {
							    ${FRAGMENTS.VIDEO_FRAGMENT}
							}
							${FRAGMENTS.THEME_FRAGMENT}
						}
					}
				}
			}
		}
	}
`

export default function Page({ data, locale }) {
	const { name, description, nextProject, projectNavigationElevator, content, themeLight } = data
	const { projectElevatorText } = useGlobals((state) => ({
		projectElevatorText: getPropSafely(state, "projectElevatorText"),
	}))
	const NextProjectRef = useRef()
	const extendedTheme = useExtendedTheme()
	const router = useRouter()
	const scroll = useScrollContext()
	const bannerData = {
		name,
		description,
	}
	const bgColor = themeLight?.mainPrimary?.hex || "rgba(238, 239, 241, 1)"
	const textColor = themeLight?.mainSecondary?.hex || "rgb(28,28,28)"
	const nextProjectProps = useMemo(() => {
		//get project colors based on current theme mode, if not found use fallbacks
		const mode = capitalize(extendedTheme.mode)
		const bgColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainPrimary?.hex || "lightGray"
		const textColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainSecondary?.hex || "black"
		return {
			data: {
				tag: nextProject?.tag || "",
				name: nextProject?.projectLink?.link?.name || "",
				description: nextProject?.projectLink?.link?.description || "",
				video: nextProject?.projectLink?.link?.video || {},
				button: {
					text: nextProject?.projectLink?.text || "",
					variant: nextProject?.projectLink?.variant || "primary",
					slug: nextProject?.projectLink?.link?.slug || "",
				},
			},
			bgColor,
			textColor,
		}
	}, [nextProject, extendedTheme.mode])
	const onButtonClick = useCallback(
		(slug) => {
			setCssVars(document.body, { "project-color": nextProjectProps.bgColor })
			router.push(slug).then(() => {
				//reset project color on route change end
				setCssVars(document.body, { "project-color": "unset" })
			})
		},
		[scroll, nextProjectProps],
	)
	const projectNavigationElevatorData =
		projectNavigationElevator &&
		projectNavigationElevator.map((el) => {
			return {
				anchor: el?.link?.id || "",
				id: el?.link?.id || "",
				text: el?.elevatorText || "",
				afterPin: el?.afterPin || false,
			}
		})
	return (
		<>
			<StickyHeading stickyHeadingsSelectors={[]} className="hidden" />
			{projectNavigationElevatorData?.length > 0 && (
				<SingleProjectElevatorNav
					data={projectNavigationElevatorData}
					locale={locale}
					projectElevatorText={projectElevatorText}
					nextProjectProps={nextProjectProps}
					onButtonClick={onButtonClick}
				/>
			)}
			<ProjectBanner data={bannerData} animate={false} />
			<div
				style={{ height: "1px" }}
				//TODO remove in future
			/>
			<ExtendedContent content={content} locale={locale} bgColor={bgColor} textColor={textColor} />
			<NextProject
				key={router.asPath}
				ref={NextProjectRef}
				onButtonClickStart={() => {
					scroll.instance.scrollTo(NextProjectRef.current, { duration, lock: true })
				}}
				onButtonClickComplete={onButtonClick}
				{...nextProjectProps}
			/>
		</>
	)
}

export async function getStaticProps({ params, locale }) {
	const { slug } = params
	const data = await getPageData("allDesignSingleProjects", slug, propsQuery, locale)
	const { globals, cookies, footer, menu } = await getLayoutData(locale)
	return {
		props: {
			data,
			globals,
			cookies,
			locale,
			footer,
			menu,
		},
		revalidate: 120,
	}
}

export async function getStaticPaths({ locales }) {
	const paths = []
	for (const locale of locales) {
		const projectPosts = await getPages("allDesignSingleProjects", pathsQuery, locale)
		const posts = projectPosts.map((page) => ({
			params: {
				slug: page.slug,
			},
			locale,
		}))
		paths.push(...posts)
	}
	// { fallback: false } means other routes should 404.
	return {
		paths,
		fallback: false,
	}
}
